// ─── Email Actions Panel ──────────────────────────────────────────────────
// Panel for the event detail page with action buttons for sending emails.
// Each action opens a confirmation dialog. Also shows a scheduled emails timeline.

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EditNoteIcon from '@mui/icons-material/EditNote';
import {
  useScheduledEmails,
  useSendCancellation,
  useSendThankYou,
  useSendUpdate,
} from '../../hooks/useEmailLifecycle.js';
import ScheduleEmailDialog from './ScheduleEmailDialog.js';

// ─── Types ────────────────────────────────────────────────────────────────

interface EmailActionsPanelProps {
  eventId: string;
  eventStatus: string;
}

type ActionType = 'reminder' | 'update' | 'cancellation' | 'thankYou' | null;

const STATUS_COLORS: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  PENDING: 'warning',
  SENT: 'success',
  CANCELLED: 'default',
  FAILED: 'error',
};

const EMAIL_TYPE_LABELS: Record<string, string> = {
  INVITE: 'Invite',
  REMINDER: 'Reminder',
  UPDATE: 'Update',
  THANK_YOU: 'Thank You',
  CANCELLATION: 'Cancellation',
};

function formatScheduledDate(dateStr: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(dateStr));
}

// ─── Component ────────────────────────────────────────────────────────────

export default function EmailActionsPanel({
  eventId,
  eventStatus,
}: EmailActionsPanelProps) {
  const [activeAction, setActiveAction] = useState<ActionType>(null);
  const [updateMessage, setUpdateMessage] = useState('');
  const [actionResult, setActionResult] = useState<{ sent: number; failed: number } | null>(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

  const { data: scheduledData } = useScheduledEmails(eventId);
  const sendCancellation = useSendCancellation();
  const sendThankYou = useSendThankYou();
  const sendUpdate = useSendUpdate();

  const scheduledEmails = scheduledData?.scheduledEmails ?? [];
  const pendingScheduled = scheduledEmails.filter((se) => se.status === 'PENDING');

  const isActive = eventStatus === 'ACTIVE';
  const isDraft = eventStatus === 'DRAFT';
  const isCancelled = eventStatus === 'CANCELLED';
  const isCompleted = eventStatus === 'COMPLETED';
  const canSendActions = isActive || isDraft;

  const isPending =
    sendCancellation.isPending || sendThankYou.isPending || sendUpdate.isPending;

  const handleConfirm = async () => {
    setActionResult(null);
    try {
      let result: { sent: number; failed: number };
      switch (activeAction) {
        case 'cancellation':
          result = await sendCancellation.mutateAsync(eventId);
          break;
        case 'thankYou':
          result = await sendThankYou.mutateAsync(eventId);
          break;
        case 'update':
          result = await sendUpdate.mutateAsync({ eventId, message: updateMessage });
          break;
        default:
          return;
      }
      setActionResult(result);
    } catch {
      // Error shown via mutation state
    }
  };

  const handleCloseDialog = () => {
    if (isPending) return;
    setActiveAction(null);
    setActionResult(null);
    setUpdateMessage('');
    sendCancellation.reset();
    sendThankYou.reset();
    sendUpdate.reset();
  };

  const currentError =
    sendCancellation.error || sendThankYou.error || sendUpdate.error;

  const dialogTitles: Record<string, string> = {
    cancellation: 'Send Cancellation Emails',
    thankYou: 'Send Thank You Emails',
    update: 'Send Event Update',
  };

  const dialogDescriptions: Record<string, string> = {
    cancellation:
      'This will send cancellation emails to all guests who RSVPed YES or MAYBE, and mark the event as CANCELLED.',
    thankYou:
      'This will send thank you emails to all guests who RSVPed YES, and mark the event as COMPLETED.',
    update: 'Enter a message to send as an update to all guests.',
  };

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Email Actions
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ScheduleIcon />}
              onClick={() => setScheduleDialogOpen(true)}
              disabled={isCancelled || isCompleted}
            >
              Schedule
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditNoteIcon />}
              onClick={() => setActiveAction('update')}
              disabled={!canSendActions}
            >
              Send Update
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<CancelScheduleSendIcon />}
              onClick={() => setActiveAction('cancellation')}
              disabled={isCancelled || isCompleted}
            >
              Send Cancellation
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="success"
              startIcon={<VolunteerActivismIcon />}
              onClick={() => setActiveAction('thankYou')}
              disabled={isCancelled || isCompleted}
            >
              Send Thank You
            </Button>
          </Stack>

          {/* Scheduled emails timeline */}
          {pendingScheduled.length > 0 && (
            <Box mt={2}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Upcoming Scheduled Emails
              </Typography>
              <Stack spacing={0.5} mt={1}>
                {pendingScheduled.slice(0, 5).map((se) => (
                  <Stack
                    key={se.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ pl: 1 }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'warning.main',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {EMAIL_TYPE_LABELS[se.emailType] ?? se.emailType} &mdash;{' '}
                      {formatScheduledDate(se.scheduledFor)}
                    </Typography>
                    <Chip
                      label={se.status}
                      size="small"
                      color={STATUS_COLORS[se.status] ?? 'default'}
                      variant="outlined"
                      sx={{ height: 18, fontSize: 10 }}
                    />
                  </Stack>
                ))}
                {pendingScheduled.length > 5 && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 3 }}>
                    +{pendingScheduled.length - 5} more
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={activeAction !== null && activeAction !== 'reminder'}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {activeAction ? dialogTitles[activeAction] ?? '' : ''}
        </DialogTitle>
        <DialogContent>
          {!actionResult && !isPending && (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                {activeAction ? dialogDescriptions[activeAction] ?? '' : ''}
              </DialogContentText>

              {activeAction === 'update' && (
                <TextField
                  label="Update Message"
                  value={updateMessage}
                  onChange={(e) => setUpdateMessage(e.target.value)}
                  multiline
                  rows={4}
                  fullWidth
                  placeholder="Type your update message here..."
                />
              )}
            </>
          )}

          {isPending && (
            <Stack alignItems="center" spacing={2} py={2}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Sending emails...
              </Typography>
            </Stack>
          )}

          {actionResult && (
            <Alert severity={actionResult.failed > 0 ? 'warning' : 'success'} sx={{ mt: 1 }}>
              {actionResult.sent} email(s) sent
              {actionResult.failed > 0 ? `, ${actionResult.failed} failed` : ''}
            </Alert>
          )}

          {currentError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {(currentError as Error)?.message ?? 'An error occurred'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {!actionResult ? (
            <>
              <Button onClick={handleCloseDialog} disabled={isPending}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                variant="contained"
                color={activeAction === 'cancellation' ? 'error' : 'primary'}
                disabled={
                  isPending || (activeAction === 'update' && !updateMessage.trim())
                }
              >
                Confirm
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseDialog} variant="contained">
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <ScheduleEmailDialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        eventId={eventId}
      />
    </>
  );
}
